import { PaymentMethodSettings } from '@kotipizzagroup/kotipizza-payments-api-admin-client';
import { RphRestaurantInformation, WebshopShopDto } from '@kotipizzagroup/kotipizza-shops-api-admin-client';

const createAdyenEcomMerchantId = (vatId?: string): string => {
  return vatId ? `${vatId}_ECOM` : '';
};

const createAdyenPosMerchantId = (vatId?: string): string => {
  return vatId ? `${vatId}_KASSA` : '';
};

const createEdenredMerchantId = (shopExternalId: string, vatId?: string): string => {
  return vatId ? `${vatId}_${shopExternalId}_EDR` : '';
};

const getDefaultStoreDescription = (restaurant: WebshopShopDto): string => {
  return restaurant.displayName?.trim() ?? '';
};

const getDefaultStorePhone = (restaurant: WebshopShopDto): string => {
  const phone = restaurant?.phone?.replaceAll(' ', '') || '';
  return phone.startsWith('+358') ? phone.trim() : `+358${phone}`.trim();
};

const getDefaultStoreStreet = (restaurant: WebshopShopDto): string => {
  return restaurant?.streetAddress?.trim() ?? '';
};

const getDefaultStoreCity = (restaurant: WebshopShopDto): string => {
  return restaurant?.city?.trim() ?? '';
};

const getDefaultStorePostalCode = (restaurant: WebshopShopDto): string => {
  return restaurant?.zip?.trim() ?? '';
};

const getDefaultUsername = (restaurant: RphRestaurantInformation): string => {
  const email = restaurant?.entrepreneurEmail;
  return email ? email.split('@')[0].trim() : '';
};

const getDefaultEmail = (restaurant: RphRestaurantInformation): string => {
  const email = restaurant?.entrepreneurEmail;
  return email?.trim() || '';
};

export const onboardUtils = {
  createAdyenEcomMerchantId,
  createAdyenPosMerchantId,
  createEdenredMerchantId,
  getDefaultStoreDescription,
  getDefaultStorePhone,
  getDefaultStoreStreet,
  getDefaultStoreCity,
  getDefaultStorePostalCode,
  getDefaultUsername,
  getDefaultEmail,
};

type Method = {
  key: AdyenMethodType;
  name: string;
};

const adyenPaymentMethods: Record<AdyenMethodType, Method> = {
  amex: {
    key: 'amex',
    name: 'Amex',
  },
  applepay: {
    key: 'applepay',
    name: 'Apple Pay',
  },
  ebanking_FI: {
    key: 'ebanking_FI',
    name: 'Verkkopankki',
  },
  givex: {
    key: 'givex',
    name: 'Givex',
  },
  googlepay: {
    key: 'googlepay',
    name: 'Google Pay',
  },
  mc: {
    key: 'mc',
    name: 'Mastercard',
  },
  mobilepay: {
    key: 'mobilepay',
    name: 'MobilePay',
  },
  visa: {
    key: 'visa',
    name: 'Visa',
  },
};

export type AdyenMethodType = 'ebanking_FI' | 'visa' | 'mobilepay' | 'mc' | 'amex' | 'applepay' | 'googlepay' | 'givex';
export type AdyenEcomMethodType = 'ebanking_FI' | 'visa' | 'mobilepay' | 'mc' | 'amex' | 'applepay' | 'googlepay' | 'givex';
export type AdyenPosMethodType = 'visa' | 'mc' | 'amex' | 'givex';

export const adyenEcomMethods: AdyenMethodType[] = [
  adyenPaymentMethods.amex.key,
  adyenPaymentMethods.applepay.key,
  adyenPaymentMethods.ebanking_FI.key,
  adyenPaymentMethods.givex.key,
  adyenPaymentMethods.googlepay.key,
  adyenPaymentMethods.mc.key,
  adyenPaymentMethods.mobilepay.key,
  adyenPaymentMethods.visa.key,
];

export const adyenPosMethods: AdyenMethodType[] = [
  adyenPaymentMethods.amex.key,
  adyenPaymentMethods.givex.key,
  adyenPaymentMethods.mc.key,
  adyenPaymentMethods.visa.key,
];

export const getMethodLabel = (key: AdyenMethodType): string => {
  return adyenPaymentMethods[key].name;
};

type ErrorObj = {
  error: string;
};

export const hasErrorKey = (errorObj: unknown): errorObj is ErrorObj => {
  return Boolean((errorObj as ErrorObj)?.error) && typeof (errorObj as ErrorObj).error === 'string';
};

export const isMethodAlreadyRequested = (
  type: AdyenMethodType,
  settings: PaymentMethodSettings[],
  storeId?: string
): boolean => {
  const method = settings.find((m) => m.type === type);
  if (!method) return false;
  if (storeId) return Boolean(method.enabled && method.storeIds?.includes(storeId));
  return Boolean(method.enabled);
};
